import { SafeLocalStorage } from '#pplx/frontend/browser-storage/localStorage.js';
import { MCP_TOOLS_SETTINGS_LS_KEY } from '#pplx/frontend/comet/client/consts.js';
import { getInlineAssistantDraggableRegionId, getSidecarThreadId, } from '#pplx/frontend/comet/client/utils.js';
import { startTimer } from '#pplx/frontend/metrics/browser.js';
import { isEqual } from 'lodash-es';
import { create } from 'zustand';
export function createStore() {
    return create((set, get) => ({
        platformInfo: undefined,
        sidecarSourceTab: {
            url: '/',
            title: '',
            tabId: -1,
            secondaryTab: null,
        },
        sidecarAutoOpenedThreads: {},
        screenshotToolEnabled: true,
        activeTab: {
            url: '/',
            title: '',
            tabId: -1,
        },
        voiceAssistantAssociatedWindowId: -1,
        voiceAssistantActiveTab: {
            url: '/',
            title: '',
            tabId: -1,
        },
        sidecarUrlById: new Map(),
        tasksState: {},
        navigationResults: {},
        classifierResults: {},
        omniboxInput: {},
        navigationMeta: {},
        taskScreenshots: {},
        taskComputerActions: {},
        userSelection: undefined,
        userSelectionByThreadId: new Map(),
        mcpStdioServers: undefined,
        mcpTools: undefined,
        mcpDisabledServers: undefined,
        installedDxts: undefined,
        taskTabs: undefined,
        mcpToolsSettings: undefined,
        pendingTasks: new Map(),
        inlineAssistantParams: {
            action: undefined,
            isWritingMode: false,
            canInsert: true,
        },
        inlineAssistantDraggableRegions: new Map(),
        navigationResultTimers: {},
        attachedTabs: [],
        actions: {
            setPlatformInfo: (platform) => {
                set({ platformInfo: platform });
            },
            setSidecarUrlById: (sidecarThreadId, url, updatedAt) => {
                const map = get().sidecarUrlById;
                map.set(sidecarThreadId, {
                    url,
                    updatedAt: updatedAt ?? Date.now(),
                });
                set({ sidecarUrlById: map });
            },
            deleteSidecarUrlById: (sidecarThreadId) => {
                const map = get().sidecarUrlById;
                map.delete(sidecarThreadId);
                set({ sidecarUrlById: map });
            },
            setSourceTab: (sidecarSourceTab) => {
                const currentTab = get().sidecarSourceTab;
                // Prevent unnecessary updates if tab data hasn't changed
                if (isEqual(currentTab, sidecarSourceTab)) {
                    return;
                }
                let screenshotToolEnabled = true;
                try {
                    const tabUrl = new URL(sidecarSourceTab.url);
                    // This is internal settings page
                    const isInternalSettingsPage = (tabUrl.protocol === 'chrome:' || tabUrl.protocol === 'comet:') &&
                        tabUrl.hostname !== 'newtab';
                    // Don't activate screenshot tool on NTP or internal settings page
                    screenshotToolEnabled = !isInternalSettingsPage;
                }
                catch {
                    // Ignore
                }
                // Check if this is the same tab (tabId didn't change) but URL changed
                const isSameTab = currentTab.tabId === sidecarSourceTab.tabId &&
                    currentTab.secondaryTab?.tabId === sidecarSourceTab.secondaryTab?.tabId;
                const urlChanged = currentTab.url !== sidecarSourceTab.url;
                // Clear selection only if URL changed within the SAME tab
                // If switching to different tab, keep selection for each tab
                if (isSameTab && urlChanged && import.meta.env.VITE_IS_SIDECAR_BUILD) {
                    const threadId = getSidecarThreadId(currentTab.tabId, currentTab.secondaryTab?.tabId);
                    set((state) => {
                        const map = new Map(state.userSelectionByThreadId);
                        map.delete(threadId);
                        return {
                            sidecarSourceTab,
                            screenshotToolEnabled,
                            userSelectionByThreadId: map,
                        };
                    });
                }
                else {
                    set({ sidecarSourceTab, screenshotToolEnabled });
                }
            },
            setSidecarAutoOpened: (autoOpened, threadId) => {
                const threads = get().sidecarAutoOpenedThreads;
                if (autoOpened) {
                    set({ sidecarAutoOpenedThreads: { ...threads, [threadId]: true } });
                }
                else {
                    const { [threadId]: _, ...rest } = threads;
                    set({ sidecarAutoOpenedThreads: rest });
                }
            },
            setActiveTab: (activeTab) => {
                const current = get().activeTab;
                if (current.tabId === activeTab.tabId &&
                    current.url === activeTab.url &&
                    current.title === activeTab.title) {
                    return;
                }
                set({ activeTab });
                if (activeTab.windowId === get().voiceAssistantAssociatedWindowId) {
                    set({ voiceAssistantActiveTab: activeTab });
                }
            },
            setVoiceAssistantAssociatedWindow: (windowId) => {
                if (get().voiceAssistantAssociatedWindowId === windowId) {
                    return;
                }
                set({ voiceAssistantAssociatedWindowId: windowId });
            },
            setVoiceAssistantActiveTab: (activeTab) => {
                const current = get().voiceAssistantActiveTab;
                if (current.tabId === activeTab.tabId &&
                    current.url === activeTab.url &&
                    current.title === activeTab.title) {
                    return;
                }
                set({ voiceAssistantActiveTab: activeTab });
            },
            setLastOmniboxInput: (streamId, lastOmniboxInput) => {
                set((state) => ({
                    omniboxInput: {
                        ...state.omniboxInput,
                        [streamId]: lastOmniboxInput,
                    },
                }));
            },
            createNavigationResultsTimer: (streamId, context) => {
                set((state) => ({
                    navigationResultTimers: {
                        ...state.navigationResultTimers,
                        [streamId]: startTimer(context),
                    },
                }));
            },
            getNavigationResultsTimer: (streamId) => {
                const state = get();
                return state.navigationResultTimers[streamId];
            },
            addNavigationResults: (streamId, { results, classifierResult, ...meta }) => {
                if (!results?.length)
                    return;
                set((state) => {
                    state.actions
                        .getNavigationResultsTimer(streamId)
                        ?.addTimingOnce('web.frontend.comet_navigation_results_received_ms', {
                        count: results.length,
                    });
                    const updatedState = {
                        navigationResults: {
                            ...state.navigationResults,
                            [streamId]: results,
                        },
                    };
                    if (classifierResult) {
                        updatedState.classifierResults = {
                            ...state.classifierResults,
                            [streamId]: classifierResult,
                        };
                    }
                    if (meta && !state.navigationMeta[streamId]) {
                        updatedState.navigationMeta = {
                            ...state.navigationMeta,
                            [streamId]: meta,
                        };
                    }
                    return updatedState;
                });
            },
            addTaskScreenshot: (taskUuid, screenshot) => {
                set((state) => ({
                    taskScreenshots: {
                        ...state.taskScreenshots,
                        [taskUuid]: [
                            ...(state.taskScreenshots[taskUuid] || []),
                            screenshot.startsWith('data:image/')
                                ? screenshot
                                : `data:image/png;base64,${screenshot}`,
                        ],
                    },
                }));
            },
            addComputerAction: (uuid, action) => {
                set((state) => ({
                    taskComputerActions: {
                        ...state.taskComputerActions,
                        [uuid]: [...(state.taskComputerActions[uuid] || []), action],
                    },
                }));
            },
            setTaskState: (taskId, isPaused) => {
                set((state) => {
                    const updatedTasksState = { ...state.tasksState };
                    if (isPaused === undefined) {
                        delete updatedTasksState[taskId];
                    }
                    else {
                        updatedTasksState[taskId] = { is_paused: isPaused };
                    }
                    return { tasksState: updatedTasksState };
                });
            },
            setUserSelection: (userSelection) => {
                if (import.meta.env.VITE_IS_SIDECAR_BUILD) {
                    const state = get();
                    const threadId = getSidecarThreadId(state.sidecarSourceTab.tabId, state.sidecarSourceTab.secondaryTab?.tabId);
                    set((state) => {
                        const map = new Map(state.userSelectionByThreadId);
                        map.set(threadId, userSelection);
                        return { userSelectionByThreadId: map };
                    });
                }
                else {
                    set({ userSelection });
                }
            },
            clearUserSelection: () => {
                if (import.meta.env.VITE_IS_SIDECAR_BUILD) {
                    const state = get();
                    const threadId = getSidecarThreadId(state.sidecarSourceTab.tabId, state.sidecarSourceTab.secondaryTab?.tabId);
                    set((state) => {
                        const map = new Map(state.userSelectionByThreadId);
                        map.delete(threadId);
                        return { userSelectionByThreadId: map };
                    });
                }
                else {
                    set({ userSelection: undefined });
                }
            },
            getUserSelection: () => {
                const state = get();
                if (import.meta.env.VITE_IS_SIDECAR_BUILD) {
                    const threadId = getSidecarThreadId(state.sidecarSourceTab.tabId, state.sidecarSourceTab.secondaryTab?.tabId);
                    return state.userSelectionByThreadId?.get(threadId);
                }
                else {
                    return state.userSelection;
                }
            },
            setMcpStdioServers: (mcpStdioServers) => {
                set({ mcpStdioServers });
            },
            setMcpTools: (serverName, mcpTools) => {
                set((state) => ({
                    mcpTools: {
                        ...state.mcpTools,
                        [serverName]: mcpTools,
                    },
                }));
            },
            removeMcpTools: (serverName) => {
                set((state) => {
                    const next = { ...state.mcpTools };
                    delete next[serverName];
                    return { mcpTools: next };
                });
            },
            setMcpDisabledServers: (mcpDisabledServers) => {
                set({ mcpDisabledServers });
                SafeLocalStorage.setItem('mcp_disabled_servers_v1', JSON.stringify(Array.from(mcpDisabledServers)));
            },
            setMcpToolsSettings: (settings) => {
                set({ mcpToolsSettings: settings });
                SafeLocalStorage.setItem(MCP_TOOLS_SETTINGS_LS_KEY, JSON.stringify(settings));
            },
            setMcpToolEnabled: (serverName, toolName, enabled) => {
                set((state) => {
                    const current = state.mcpToolsSettings || {};
                    const serverEntry = { ...(current[serverName] || {}) };
                    const prev = serverEntry[toolName] ?? {
                        enabled: true,
                    };
                    const nextServerEntry = {
                        ...serverEntry,
                        [toolName]: { ...prev, enabled },
                    };
                    const next = {
                        ...current,
                        [serverName]: nextServerEntry,
                    };
                    SafeLocalStorage.setItem(MCP_TOOLS_SETTINGS_LS_KEY, JSON.stringify(next));
                    return { mcpToolsSettings: next };
                });
            },
            renameMcpServerInToolsSettings: (oldName, newName) => {
                set((state) => {
                    const current = state.mcpToolsSettings || {};
                    if (!current[oldName] || oldName === newName) {
                        return state;
                    }
                    const next = { ...current };
                    const oldServerEntry = current[oldName];
                    // Preserve tool names; only move server entry under new name
                    next[newName] = { ...oldServerEntry };
                    delete next[oldName];
                    SafeLocalStorage.setItem(MCP_TOOLS_SETTINGS_LS_KEY, JSON.stringify(next));
                    return { mcpToolsSettings: next };
                });
            },
            setInstalledDxts: (installedDxts) => {
                set({ installedDxts });
            },
            setTaskTabs: (taskTabs) => {
                set({ taskTabs });
            },
            setPendingTask: (taskId, handler) => {
                set((state) => {
                    const pendingTasks = new Map(state.pendingTasks);
                    pendingTasks.set(taskId, handler);
                    return { pendingTasks };
                });
            },
            deletePendingTask: (taskId) => {
                set((state) => {
                    const pendingTasks = new Map(state.pendingTasks);
                    pendingTasks.delete(taskId);
                    return { pendingTasks };
                });
            },
            getPendingTask: (taskId) => {
                return get().pendingTasks.get(taskId);
            },
            setInlineAssistantParams: (params) => {
                set((state) => ({
                    inlineAssistantParams: {
                        ...state.inlineAssistantParams,
                        ...params,
                    },
                }));
            },
            setInlineAssistantDraggableRegion: (region) => {
                const id = getInlineAssistantDraggableRegionId(region);
                const x = Math.floor(region.origin.x);
                const y = Math.floor(region.origin.y);
                const width = Math.floor(region.size.width);
                const height = Math.floor(region.size.height);
                set((state) => {
                    const inlineAssistantDraggableRegions = new Map(state.inlineAssistantDraggableRegions);
                    inlineAssistantDraggableRegions.set(id, {
                        origin: { x, y },
                        size: { width, height },
                    });
                    return { inlineAssistantDraggableRegions };
                });
            },
            removeInlineAssistantDraggableRegion: (region) => {
                const id = getInlineAssistantDraggableRegionId(region);
                set((state) => {
                    const inlineAssistantDraggableRegions = new Map(state.inlineAssistantDraggableRegions);
                    inlineAssistantDraggableRegions.delete(id);
                    return { inlineAssistantDraggableRegions };
                });
            },
            addAttachedTab: (tab) => {
                set((state) => {
                    if (state.attachedTabs.some((t) => t.id === tab.id)) {
                        return state;
                    }
                    return { attachedTabs: [...state.attachedTabs, tab] };
                });
            },
            removeAttachedTab: (tabId) => {
                set((state) => ({
                    attachedTabs: state.attachedTabs.filter((t) => t.id !== tabId),
                }));
            },
            clearAttachedTabs: () => {
                set({ attachedTabs: [] });
            },
        },
    }));
}
//# sourceMappingURL=store.js.map